import React, { useRef, useState } from "react";
import { exportAnnotatedDicom, DicomExportError } from "../utils/exportAnnotatedDicom";
import { tryReadAnnotationsFromDicom } from "../utils/readAnnotationsFromDicom";
import { cornerstone, cornerstoneWADOImageLoader } from "../initCornerstone";
import dcmjs from "dcmjs";



const DicomViewer = () => {
  const elementRef = useRef(null);
  const fileInputRef = useRef(null);
  const [error, setError] = useState(null);
  const [exportStatus, setExportStatus] = useState(null); // 'loading', 'success', 'error'
  const [exportMessage, setExportMessage] = useState("");
  const [metadata, setMetadata] = useState({});
  const [numFrames, setNumFrames] = useState(1);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [baseImageId, setBaseImageId] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [playing, setPlaying] = useState(false);
  const playIntervalRef = useRef(null);

  // ✅ Controls
  const [zoom, setZoom] = useState(1);
  const [contrast, setContrast] = useState(1000);
  const [speed, setSpeed] = useState(1); // 0.25 → 2x

  // 🏷️ Tagging state
  const [tags, setTags] = useState({}); // { frameIndex: [{x, y, label}] }
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagPosition, setTagPosition] = useState({ x: 0, y: 0 });
  const [savedFrames, setSavedFrames] = useState(new Set()); // Track which frames are saved
  const [showSavePrompt, setShowSavePrompt] = useState(false);

  // 🎨 Drawing tools state
  const [annotations, setAnnotations] = useState({}); // { frameIndex: [{type, data, color, id}] }
  const [currentTool, setCurrentTool] = useState('tag'); // 'tag', 'line', 'rectangle', 'sketch'
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentDrawing, setCurrentDrawing] = useState(null);
  const [drawingColor, setDrawingColor] = useState('#22D3EE');
  const [sketchPoints, setSketchPoints] = useState([]);

  const displayMetadata = (dataset) => {
    setMetadata({
      PatientName: dataset.string("x00100010") || "N/A",
      PatientID: dataset.string("x00100020") || "N/A",
      PatientBirthDate: dataset.string("x00100030") || "N/A",
      PatientSex: dataset.string("x00100040") || "N/A",
      StudyInstanceUID: dataset.string("x0020000D") || "N/A",
      StudyID: dataset.string("x00200010") || "N/A",
      StudyDate: dataset.string("x00080020") || "N/A",
      StudyTime: dataset.string("x00080030") || "N/A",
      StudyDescription: dataset.string("x00081030") || "N/A",
      AccessionNumber: dataset.string("x00080050") || "N/A",
      Modality: dataset.string("x00080060") || "N/A",
      Manufacturer: dataset.string("x00080070") || "N/A",
      ManufacturerModelName: dataset.string("x00081090") || "N/A",
      DeviceSerialNumber: dataset.string("x00181000") || "N/A",
      SoftwareVersions: dataset.string("x00181020") || "N/A",
      ProtocolName: dataset.string("x00181030") || "N/A",
      Rows: dataset.uint16("x00280010") || "N/A",
      Columns: dataset.uint16("x00280011") || "N/A",
      NumberOfFrames: dataset.string("x00280008") || "1",
      BitsAllocated: dataset.uint16("x00280100") || "N/A",
      BitsStored: dataset.uint16("x00280101") || "N/A",
      PixelRepresentation: dataset.uint16("x00280103") || "N/A",
      PhotometricInterpretation: dataset.string("x00280004") || "N/A",
      SamplesPerPixel: dataset.uint16("x00280002") || "N/A",
      PixelSpacing: dataset.string("x00280030") || "N/A",
      SOPClassUID: dataset.string("x00080016") || "N/A",
      SOPInstanceUID: dataset.string("x00080018") || "N/A",
      TransferSyntaxUID: dataset.string("x00020010") || "N/A",
      ImplementationClassUID: dataset.string("x00020012") || "N/A",
      ImplementationVersionName: dataset.string("x00020013") || "N/A",
    });
  };

  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setError(null);
    setExportStatus(null);
    setExportMessage("");
    clearInterval(playIntervalRef.current);
    setPlaying(false);

    try {
      const file = files[0];
      
      // Basic file validation
      if (file.size === 0) {
        setError("The selected file is empty. Please choose a valid DICOM file.");
        return;
      }
      
      if (file.size > 500 * 1024 * 1024) { // 500MB limit
        setError("The selected file is too large (>500MB). Please choose a smaller DICOM file.");
        return;
      }
      
      setOriginalFile(file);
      const id = cornerstoneWADOImageLoader.wadouri.fileManager.add(file);
      setBaseImageId(id);

      const element = elementRef.current;
      cornerstone.enable(element);

      const image = await cornerstone.loadAndCacheImage(id);
      cornerstone.displayImage(element, image);

      const dataset = image.data;
      
      // Validate essential DICOM metadata
      if (!dataset.string("x00080016")) { // SOPClassUID
        setError("The file appears to be missing essential DICOM metadata. Please verify it's a valid DICOM file.");
        return;
      }
      
      displayMetadata(dataset);

      const frames = parseInt(dataset.string("x00280008")) || 1;
      setNumFrames(frames);
      setCurrentFrame(0);
      
      // Try to read annotations from the DICOM file
      // Get image dimensions directly from the dataset
      const cols = dataset.uint16("x00280011") || 0; // Columns
      const rows = dataset.uint16("x00280010") || 0; // Rows
      
      const imageMetadata = { cols, rows };
      
      console.log("Image metadata for annotation reading:", imageMetadata);
      console.log("Dataset dimension values:", {
        columnsRaw: dataset.elements.x00280011,
        rowsRaw: dataset.elements.x00280010,
        columnsUint16: dataset.uint16("x00280011"),
        rowsUint16: dataset.uint16("x00280010")
      });
      
      // Try to read annotations from the DICOM file
      // We need to re-parse the original file to get access to private tags
      let annotationResult = { success: false, annotations: {}, stats: { framesWithAnnotations: 0, totalAnnotations: 0 } };
      
      try {
        // Re-read the file to get full DICOM data including private tags
        const arrayBuffer = await file.arrayBuffer();
        const dicomMessage = dcmjs.data.DicomMessage.readFile(arrayBuffer);
        annotationResult = tryReadAnnotationsFromDicom(dicomMessage, imageMetadata);
      } catch (e) {
        console.warn("Could not re-parse DICOM file for annotations:", e);
      }
      
      if (annotationResult.success && Object.keys(annotationResult.annotations).length > 0) {
        console.log("Found embedded annotations:", annotationResult.stats);
        
        // Separate tags and geometric annotations
        const loadedTags = {};
        const loadedAnnotations = {};
        
        Object.entries(annotationResult.annotations).forEach(([frameKey, frameAnnotations]) => {
          const frameTags = [];
          const frameGeometricAnnotations = [];
          
          frameAnnotations.forEach(annotation => {
            if (annotation.type && ['line', 'rectangle', 'sketch'].includes(annotation.type)) {
              // This is a geometric annotation
              frameGeometricAnnotations.push(annotation);
            } else if (annotation.ix !== undefined && annotation.iy !== undefined && annotation.label) {
              // This is a tag annotation
              frameTags.push(annotation);
            }
          });
          
          if (frameTags.length > 0) {
            loadedTags[frameKey] = frameTags;
          }
          if (frameGeometricAnnotations.length > 0) {
            loadedAnnotations[frameKey] = frameGeometricAnnotations;
          }
        });
        
        setTags(loadedTags);
        setAnnotations(loadedAnnotations);
        
        // Mark frames with annotations as saved since they came from the file
        const framesWithAnnotations = Object.keys(annotationResult.annotations).map(k => parseInt(k, 10));
        setSavedFrames(new Set(framesWithAnnotations));
        
        // Show success message to user
        const totalTags = Object.values(loadedTags).reduce((sum, tags) => sum + tags.length, 0);
        const totalGeometric = Object.values(loadedAnnotations).reduce((sum, annotations) => sum + annotations.length, 0);
        
        setExportStatus('success');
        setExportMessage(`✅ Loaded DICOM file with ${totalTags} tags and ${totalGeometric} drawings across ${annotationResult.stats.framesWithAnnotations} frames`);
        
        // Clear the message after a few seconds
        setTimeout(() => {
          setExportStatus(null);
          setExportMessage("");
        }, 5000);
      } else {
        // No annotations found, start fresh
        setTags({});
        setAnnotations({});
        setSavedFrames(new Set());
        
        if (annotationResult.error) {
          console.log("No annotations found in DICOM file:", annotationResult.error);
        } else {
          console.log("No annotations found in DICOM file");
        }
      }
      
    } catch (err) {
      console.error("File loading error:", err);
      
      // Provide user-friendly error messages based on error type
      let userMessage = "Failed to load DICOM file. ";
      
      if (err.message?.includes("not a valid DICOM")) {
        userMessage += "The file is not a valid DICOM file or is corrupted.";
      } else if (err.message?.includes("network") || err.message?.includes("fetch")) {
        userMessage += "Network error occurred while loading the file.";
      } else if (err.message?.includes("memory") || err.message?.includes("allocation")) {
        userMessage += "The file is too large to process in memory.";
      } else if (err.message?.includes("unsupported")) {
        userMessage += "This DICOM format is not supported.";
      } else {
        userMessage += "Please check that the file is a valid DICOM file and try again.";
      }
      
      setError(userMessage);
    }
  };

  const showFrame = async (frameIndex) => {
    if (!baseImageId) return;
    try {
      const frameImageId = `${baseImageId}?frame=${frameIndex}`;
      const image = await cornerstone.loadAndCacheImage(frameImageId);
      cornerstone.displayImage(elementRef.current, image);
      setCurrentFrame(frameIndex);
    } catch (err) {
      console.error("Frame loading error:", err);
      
      let userMessage = `Failed to load frame ${frameIndex + 1}. `;
      if (err.message?.includes("not found") || err.message?.includes("invalid frame")) {
        userMessage += "The requested frame does not exist in this DICOM file.";
      } else if (err.message?.includes("corrupted") || err.message?.includes("decode")) {
        userMessage += "The frame data appears to be corrupted.";
      } else {
        userMessage += "Please try a different frame or reload the file.";
      }
      
      setError(userMessage);
      clearInterval(playIntervalRef.current);
      setPlaying(false);
    }
  };

  // 🟢 Play/Pause logic
  const handlePlay = () => {
    if (numFrames <= 1) return;

    if (playing) {
      clearInterval(playIntervalRef.current);
      setPlaying(false);
    } else {
      // Check if there are unsaved tagged frames or annotations
      const hasUnsavedTags = Object.keys(tags).some(
        frameIdx => tags[frameIdx].length > 0 && !savedFrames.has(parseInt(frameIdx))
      );
      const hasUnsavedAnnotations = Object.keys(annotations).some(
        frameIdx => annotations[frameIdx].length > 0 && !savedFrames.has(parseInt(frameIdx))
      );
      
      if (hasUnsavedTags || hasUnsavedAnnotations) {
        setShowSavePrompt(true);
        return;
      }
      
      startPlaying();
    }
  };

  const startPlaying = () => {
    setPlaying(true);
    const interval = 1000 / (speed * 4);
    playIntervalRef.current = setInterval(() => {
      setCurrentFrame((prev) => {
        const next = (prev + 1) % numFrames;
        showFrame(next);
        return next;
      });
    }, interval);
  };

  const handleSavePromptResponse = (shouldSave) => {
    if (shouldSave) {
      // Save all tagged frames and annotated frames
      const framesToSave = new Set();
      Object.keys(tags).forEach(frameIdx => {
        if (tags[frameIdx].length > 0) {
          framesToSave.add(parseInt(frameIdx));
        }
      });
      Object.keys(annotations).forEach(frameIdx => {
        if (annotations[frameIdx].length > 0) {
          framesToSave.add(parseInt(frameIdx));
        }
      });
      
      framesToSave.forEach(frameIdx => {
        handleDownloadFrame(frameIdx);
      });
    }
    setShowSavePrompt(false);
    startPlaying();
  };

  const handleSliderChange = (e) => {
    const frameIndex = parseInt(e.target.value);
    showFrame(frameIndex);
  };

  // ✅ Zoom / Contrast / Speed
  const handleZoomChange = (e) => {
    const newZoom = parseFloat(e.target.value);
    setZoom(newZoom);
    const viewport = cornerstone.getViewport(elementRef.current);
    viewport.scale = newZoom;
    cornerstone.setViewport(elementRef.current, viewport);
  };

  const handleContrastChange = (e) => {
    const newContrast = parseFloat(e.target.value);
    setContrast(newContrast);
    const viewport = cornerstone.getViewport(elementRef.current);
    viewport.voi.windowWidth = newContrast;
    cornerstone.setViewport(elementRef.current, viewport);
  };

  const handleSpeedChange = (e) => {
    const newSpeed = parseFloat(e.target.value);
    setSpeed(newSpeed);
    if (playing) {
      clearInterval(playIntervalRef.current);
      const interval = 1000 / (newSpeed * 4);
      playIntervalRef.current = setInterval(() => {
        setCurrentFrame((prev) => {
          const next = (prev + 1) % numFrames;
          showFrame(next);
          return next;
        });
      }, interval);
    }
  };

  // 🏷️ Tagging and Drawing
  const handleImageClick = (e) => {
    if (!elementRef.current || playing) return;
    const rect = elementRef.current.getBoundingClientRect();
    const domX = e.clientX - rect.left;
    const domY = e.clientY - rect.top;

    // normalized coordinates relative to viewer DOM (0..1)
    const nx = domX / rect.width;
    const ny = domY / rect.height;

    if (currentTool === 'tag') {
      setTagPosition({ x: domX, y: domY }); // for popup placement
      // temporarily store normalized in tagPosition as well so handleAddTag can use it
      setTagPosition(prev => ({ ...prev, nx, ny }));
      setShowTagInput(true);
    }
  };

  const handleMouseDown = (e) => {
    if (!elementRef.current || playing || currentTool === 'tag') return;
    
    const rect = elementRef.current.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;

    setIsDrawing(true);

    if (currentTool === 'line' || currentTool === 'rectangle') {
      setCurrentDrawing({
        type: currentTool,
        startX: nx,
        startY: ny,
        endX: nx,
        endY: ny,
        color: drawingColor,
        id: Date.now()
      });
    } else if (currentTool === 'sketch') {
      setSketchPoints([{ x: nx, y: ny }]);
      setCurrentDrawing({
        type: 'sketch',
        points: [{ x: nx, y: ny }],
        color: drawingColor,
        id: Date.now()
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !elementRef.current || currentTool === 'tag') return;

    const rect = elementRef.current.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;

    if (currentTool === 'line' || currentTool === 'rectangle') {
      setCurrentDrawing(prev => ({
        ...prev,
        endX: nx,
        endY: ny
      }));
    } else if (currentTool === 'sketch') {
      const newPoint = { x: nx, y: ny };
      setSketchPoints(prev => [...prev, newPoint]);
      setCurrentDrawing(prev => ({
        ...prev,
        points: [...prev.points, newPoint]
      }));
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing || currentTool === 'tag') return;

    if (currentDrawing) {
      // Add the completed drawing to annotations
      setAnnotations(prev => ({
        ...prev,
        [currentFrame]: [...(prev[currentFrame] || []), currentDrawing]
      }));

      // Mark frame as unsaved
      setSavedFrames(prev => {
        const newSet = new Set(prev);
        newSet.delete(currentFrame);
        return newSet;
      });
    }

    setIsDrawing(false);
    setCurrentDrawing(null);
    setSketchPoints([]);
  };


  const handleAddTag = (label) => {
  if (!label || !label.trim()) {
    setShowTagInput(false);
    return;
  }
  // tagPosition now has nx, ny (normalized)
  const ix = tagPosition.nx ?? (tagPosition.x / (elementRef.current?.getBoundingClientRect().width || 1));
  const iy = tagPosition.ny ?? (tagPosition.y / (elementRef.current?.getBoundingClientRect().height || 1));

  setTags((prev) => ({
    ...prev,
    [currentFrame]: [
      ...(prev[currentFrame] || []),
      { ix, iy, label: label.trim() },
    ],
  }));

  // Mark frame as unsaved when a new tag is added
  setSavedFrames(prev => {
    const newSet = new Set(prev);
    newSet.delete(currentFrame);
    return newSet;
  });
  setShowTagInput(false);
};


  const handleRemoveTag = (tagIndex) => {
    setTags((prev) => {
      const newTags = { ...prev };
      newTags[currentFrame] = newTags[currentFrame].filter((_, i) => i !== tagIndex);
      if (newTags[currentFrame].length === 0) {
        delete newTags[currentFrame];
      }
      return newTags;
    });
    // Mark frame as unsaved when a tag is removed
    setSavedFrames(prev => {
      const newSet = new Set(prev);
      newSet.delete(currentFrame);
      return newSet;
    });
  };

  const handleRemoveAnnotation = (annotationId) => {
    setAnnotations(prev => {
      const newAnnotations = { ...prev };
      newAnnotations[currentFrame] = newAnnotations[currentFrame].filter(ann => ann.id !== annotationId);
      if (newAnnotations[currentFrame].length === 0) {
        delete newAnnotations[currentFrame];
      }
      return newAnnotations;
    });
    // Mark frame as unsaved when an annotation is removed
    setSavedFrames(prev => {
      const newSet = new Set(prev);
      newSet.delete(currentFrame);
      return newSet;
    });
  };

  const clearAllAnnotations = () => {
    setAnnotations(prev => {
      const newAnnotations = { ...prev };
      delete newAnnotations[currentFrame];
      return newAnnotations;
    });
    setTags(prev => {
      const newTags = { ...prev };
      delete newTags[currentFrame];
      return newTags;
    });
    setSavedFrames(prev => {
      const newSet = new Set(prev);
      newSet.delete(currentFrame);
      return newSet;
    });
  };

  // 💾 Download functionality
  const handleDownloadFrame = (frameIndex = currentFrame) => {
    if (!elementRef.current) return;

    try {
      const canvas = elementRef.current.querySelector('canvas');
      if (!canvas) {
        setError("No image to download");
        return;
      }

      // Create a new canvas to draw the image and tags
      const downloadCanvas = document.createElement('canvas');
      downloadCanvas.width = canvas.width;
      downloadCanvas.height = canvas.height;
      const ctx = downloadCanvas.getContext('2d');

      // Draw the original image
      ctx.drawImage(canvas, 0, 0);

      // Draw annotations and tags on top
      const frameTags = tags[frameIndex] || [];
      const frameAnnotations = annotations[frameIndex] || [];

      // Draw geometric annotations first
      frameAnnotations.forEach(annotation => {
        ctx.strokeStyle = annotation.color;
        ctx.fillStyle = annotation.color;
        ctx.lineWidth = 4; // Make lines more visible in saved image
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        if (annotation.type === 'line') {
          ctx.beginPath();
          ctx.moveTo(annotation.startX * canvas.width, annotation.startY * canvas.height);
          ctx.lineTo(annotation.endX * canvas.width, annotation.endY * canvas.height);
          ctx.stroke();
        } else if (annotation.type === 'rectangle') {
          const x = Math.min(annotation.startX, annotation.endX) * canvas.width;
          const y = Math.min(annotation.startY, annotation.endY) * canvas.height;
          const width = Math.abs(annotation.endX - annotation.startX) * canvas.width;
          const height = Math.abs(annotation.endY - annotation.startY) * canvas.height;
          ctx.strokeRect(x, y, width, height);
        } else if (annotation.type === 'sketch' && annotation.points.length > 1) {
          ctx.beginPath();
          ctx.moveTo(annotation.points[0].x * canvas.width, annotation.points[0].y * canvas.height);
          for (let i = 1; i < annotation.points.length; i++) {
            ctx.lineTo(annotation.points[i].x * canvas.width, annotation.points[i].y * canvas.height);
          }
          ctx.stroke();
        }
      });

      // Draw tags on top
      const rect = elementRef.current.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      frameTags.forEach(tag => {
        // Convert normalized coordinates to canvas pixel coordinates
        const canvasX = (tag.ix || 0) * canvas.width;
        const canvasY = (tag.iy || 0) * canvas.height;
        const scaledX = canvasX;
        const scaledY = canvasY;

        // Draw dot
        ctx.fillStyle = '#22D3EE';
        ctx.beginPath();
        ctx.arc(scaledX, scaledY, 8, 0, 2 * Math.PI); // Fixed size for consistency
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 3;
        ctx.stroke();

        // Draw label background
        ctx.font = `${Math.max(16, 14 * scaleX)}px sans-serif`;
        const metrics = ctx.measureText(tag.label);
        const padding = 6;
        const labelX = scaledX - metrics.width / 2;
        const labelY = scaledY - 25;
        
        ctx.fillStyle = '#0891B2';
        ctx.fillRect(
          labelX - padding,
          labelY - 18,
          metrics.width + padding * 2,
          22
        );
        
        // Draw label text
        ctx.fillStyle = 'white';
        ctx.fillText(tag.label, labelX, labelY);
      });

      // Download
      downloadCanvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dicom_frame_${frameIndex + 1}_annotated.png`;
        a.click();
        URL.revokeObjectURL(url);
      });

      // Mark frame as saved
      setSavedFrames(prev => new Set(prev).add(frameIndex));
    } catch (err) {
      console.error("Download error:", err);
      
      let userMessage = "Failed to download image. ";
      if (err.message?.includes("canvas") || err.message?.includes("context")) {
        userMessage += "Unable to access the image canvas for download.";
      } else if (err.message?.includes("blob") || err.message?.includes("URL")) {
        userMessage += "Browser blocked the download or ran out of memory.";
      } else {
        userMessage += "Please try again or check your browser's download settings.";
      }
      
      setError(userMessage);
    }
  };

  const handleDownload = () => {
    handleDownloadFrame(currentFrame);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white flex flex-col items-center py-6">
      <h1 className="text-3xl font-bold mb-6 text-cyan-400 tracking-wide drop-shadow-lg">
        🩺 DICOM Image Viewer
      </h1>

      {/* Drawing Tools Toolbar */}
      {baseImageId && (
        <div className="mb-4 flex flex-wrap gap-3 items-center justify-center bg-gray-800/50 p-4 rounded-xl border border-gray-700">
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentTool('tag')}
              className={`px-4 py-2 rounded-lg transition ${
                currentTool === 'tag' 
                  ? 'bg-cyan-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              🏷️ Tag
            </button>
            <button
              onClick={() => setCurrentTool('line')}
              className={`px-4 py-2 rounded-lg transition ${
                currentTool === 'line' 
                  ? 'bg-cyan-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              📏 Line
            </button>
            <button
              onClick={() => setCurrentTool('rectangle')}
              className={`px-4 py-2 rounded-lg transition ${
                currentTool === 'rectangle' 
                  ? 'bg-cyan-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              ⬜ Rectangle
            </button>
            <button
              onClick={() => setCurrentTool('sketch')}
              className={`px-4 py-2 rounded-lg transition ${
                currentTool === 'sketch' 
                  ? 'bg-cyan-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              ✏️ Sketch
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-300">Color:</label>
            <input
              type="color"
              value={drawingColor}
              onChange={(e) => setDrawingColor(e.target.value)}
              className="w-8 h-8 rounded border border-gray-600 cursor-pointer"
            />
          </div>

          <button
            onClick={clearAllAnnotations}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
            title="Clear all annotations on current frame"
          >
            🗑️ Clear All
          </button>
        </div>
      )}

      {/* Upload Section */}
<div className="mb-4 flex gap-3">
  <label className="px-5 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg cursor-pointer transition">
    Upload DICOM File
    <input
      type="file"
      ref={fileInputRef}
      accept=".dcm,.dicom,application/dicom"
      onChange={handleFileChange}
      className="hidden"
    />
  </label>

  {/* PNG download button (existing) */}
  {baseImageId && (
    <button
      onClick={handleDownload}
      className="px-5 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition"
    >
      💾 Download Annotated Image
    </button>
  )}

  {/* Enhanced Save DICOM button with comprehensive functionality */}
  {baseImageId && originalFile && (
    <button
      onClick={async () => {
        // Clear any previous error states
        setError(null);
        setExportStatus('loading');
        setExportMessage("Initializing DICOM export...");
        
        try {
          // Validate that we have annotations to export
          const hasTagAnnotations = Object.keys(tags || {}).some(
            frameKey => tags[frameKey] && tags[frameKey].length > 0
          );
          const hasDrawingAnnotations = Object.keys(annotations || {}).some(
            frameKey => annotations[frameKey] && annotations[frameKey].length > 0
          );
          const hasAnnotations = hasTagAnnotations || hasDrawingAnnotations;
          
          if (!hasAnnotations) {
            setExportStatus('error');
            setExportMessage("No annotations found to export. Please add some tags to frames before exporting.");
            setTimeout(() => {
              setExportStatus(null);
              setExportMessage("");
            }, 5000);
            return;
          }
          
          // Update progress message
          setExportMessage("Processing annotations and preparing DICOM file...");
          
          // Combine tags and drawing annotations for export
          const combinedAnnotations = {};
          
          // Add text tags
          Object.keys(tags || {}).forEach(frameKey => {
            if (tags[frameKey] && tags[frameKey].length > 0) {
              combinedAnnotations[frameKey] = [...(tags[frameKey] || [])];
            }
          });
          
          // Add drawing annotations (convert to tag-like format for export)
          Object.keys(annotations || {}).forEach(frameKey => {
            if (annotations[frameKey] && annotations[frameKey].length > 0) {
              if (!combinedAnnotations[frameKey]) {
                combinedAnnotations[frameKey] = [];
              }
              
              // Convert drawing annotations to exportable format (preserve geometric data)
              annotations[frameKey].forEach(annotation => {
                // Export geometric annotations with their full data structure
                const exportAnnotation = {
                  type: annotation.type,
                  color: annotation.color,
                  id: annotation.id
                };
                
                if (annotation.type === 'line') {
                  exportAnnotation.startX = annotation.startX;
                  exportAnnotation.startY = annotation.startY;
                  exportAnnotation.endX = annotation.endX;
                  exportAnnotation.endY = annotation.endY;
                } else if (annotation.type === 'rectangle') {
                  exportAnnotation.startX = annotation.startX;
                  exportAnnotation.startY = annotation.startY;
                  exportAnnotation.endX = annotation.endX;
                  exportAnnotation.endY = annotation.endY;
                } else if (annotation.type === 'sketch') {
                  exportAnnotation.points = annotation.points;
                }
                
                combinedAnnotations[frameKey].push(exportAnnotation);
              });
            }
          });

          // Call the export function with enhanced error handling
          const result = await exportAnnotatedDicom({ 
            file: originalFile, 
            annotationsByFrame: combinedAnnotations 
          });
          
          // Success handling with detailed feedback
          setExportStatus('success');
          const stats = result.stats || {};
          const filename = originalFile.name ? 
            originalFile.name.replace(/\.dcm$/i, "") + "_annotated.dcm" : 
            "annotated.dcm";
          
          const message = `✅ Successfully exported "${filename}" with ${stats.totalAnnotations || 0} annotations across ${stats.framesWithAnnotations || 0} frames (${Math.round((stats.outputSize || 0) / 1024)} KB)`;
          setExportMessage(message);
          
          // Mark all frames with annotations as saved
          const framesWithTags = Object.keys(tags || {})
            .filter(frameKey => tags[frameKey] && tags[frameKey].length > 0)
            .map(k => parseInt(k, 10));
          const framesWithDrawings = Object.keys(annotations || {})
            .filter(frameKey => annotations[frameKey] && annotations[frameKey].length > 0)
            .map(k => parseInt(k, 10));
          const framesWithAnnotations = [...new Set([...framesWithTags, ...framesWithDrawings])];
          
          setSavedFrames(new Set(framesWithAnnotations));
          
          // Log success for debugging
          console.log("DICOM export completed successfully:", {
            filename,
            stats,
            framesMarkedAsSaved: framesWithAnnotations
          });
          
          // Clear success message after 7 seconds
          setTimeout(() => {
            setExportStatus(null);
            setExportMessage("");
          }, 7000);
          
        } catch (err) {
          console.error("DICOM export failed:", err);
          
          setExportStatus('error');
          
          // Enhanced error handling with specific error types
          let userMessage = "Export failed: ";
          
          if (err instanceof DicomExportError) {
            userMessage = err.details?.userMessage || err.message;
            
            // Log detailed error information for debugging
            console.error("DICOM Export Error Details:", {
              code: err.code,
              details: err.details,
              originalError: err.details?.originalError
            });
          } else if (err.name === 'QuotaExceededError') {
            userMessage = "Export failed: Not enough storage space available. Please free up some space and try again.";
          } else if (err.name === 'SecurityError') {
            userMessage = "Export failed: Browser security settings prevented the download. Please check your download settings.";
          } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
            userMessage = "Export failed: Network error occurred. Please check your connection and try again.";
          } else {
            userMessage = "Export failed: An unexpected error occurred. Please try again or contact support if the problem persists.";
          }
          
          setExportMessage(userMessage);
          
          // Clear error message after 12 seconds for errors (longer than success)
          setTimeout(() => {
            setExportStatus(null);
            setExportMessage("");
          }, 12000);
        }
      }}
      disabled={exportStatus === 'loading' || !originalFile || Object.keys(tags || {}).length === 0}
      className={`px-5 py-2 rounded-lg transition font-medium ${
        exportStatus === 'loading' 
          ? 'bg-gray-500 cursor-not-allowed text-gray-300' 
          : !originalFile
          ? 'bg-gray-600 cursor-not-allowed text-gray-400'
          : (Object.keys(tags || {}).length === 0 && Object.keys(annotations || {}).length === 0)
          ? 'bg-gray-600 cursor-not-allowed text-gray-400'
          : 'bg-yellow-600 hover:bg-yellow-700 text-white shadow-lg hover:shadow-xl'
      }`}
      title={
        !originalFile 
          ? "Please load a DICOM file first" 
          : (Object.keys(tags || {}).length === 0 && Object.keys(annotations || {}).length === 0)
          ? "Please add some annotations before exporting"
          : "Export DICOM file with embedded annotations"
      }
    >
      {exportStatus === 'loading' ? (
        <>
          <span className="inline-block animate-spin mr-2">⏳</span>
          Exporting...
        </>
      ) : (
        <>
          <span className="mr-2">💾</span>
          Save DICOM w/ annotations
        </>
      )}
    </button>
  )}
</div>


      {/* Viewer + Metadata */}
      <div className="flex flex-col lg:flex-row gap-6 w-[90%] max-w-[1400px] justify-center">
        <div
          ref={elementRef}
          onClick={handleImageClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          className={`relative w-[900px] h-[900px] bg-black border-2 border-gray-700 rounded-xl shadow-2xl overflow-hidden ${
            currentTool !== 'tag' ? 'cursor-crosshair' : 'cursor-pointer'
          }`}
        >
          {!baseImageId && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-lg">
              Upload a DICOM file to view
            </div>
          )}

          {/* 🎨 Render annotations for current frame */}
          {(annotations[currentFrame] || []).map((annotation) => {
            const rect = elementRef.current?.getBoundingClientRect();
            if (!rect) return null;

            if (annotation.type === 'line') {
              const x1 = annotation.startX * rect.width;
              const y1 = annotation.startY * rect.height;
              const x2 = annotation.endX * rect.width;
              const y2 = annotation.endY * rect.height;
              
              return (
                <svg
                  key={annotation.id}
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: rect.width, height: rect.height }}
                >
                  <line
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={annotation.color}
                    strokeWidth="3"
                    className="cursor-pointer pointer-events-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveAnnotation(annotation.id);
                    }}
                    title="Click to remove"
                  />
                </svg>
              );
            } else if (annotation.type === 'rectangle') {
              const x = Math.min(annotation.startX, annotation.endX) * rect.width;
              const y = Math.min(annotation.startY, annotation.endY) * rect.height;
              const width = Math.abs(annotation.endX - annotation.startX) * rect.width;
              const height = Math.abs(annotation.endY - annotation.startY) * rect.height;
              
              return (
                <svg
                  key={annotation.id}
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: rect.width, height: rect.height }}
                >
                  <rect
                    x={x} y={y} width={width} height={height}
                    fill="none"
                    stroke={annotation.color}
                    strokeWidth="3"
                    className="cursor-pointer pointer-events-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveAnnotation(annotation.id);
                    }}
                    title="Click to remove"
                  />
                </svg>
              );
            } else if (annotation.type === 'sketch' && annotation.points.length > 1) {
              const pathData = annotation.points.map((point, index) => {
                const x = point.x * rect.width;
                const y = point.y * rect.height;
                return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
              }).join(' ');
              
              return (
                <svg
                  key={annotation.id}
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: rect.width, height: rect.height }}
                >
                  <path
                    d={pathData}
                    fill="none"
                    stroke={annotation.color}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="cursor-pointer pointer-events-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveAnnotation(annotation.id);
                    }}
                    title="Click to remove"
                  />
                </svg>
              );
            }
            return null;
          })}

          {/* 🎨 Render current drawing */}
          {currentDrawing && elementRef.current && (() => {
            const rect = elementRef.current.getBoundingClientRect();
            
            if (currentDrawing.type === 'line') {
              const x1 = currentDrawing.startX * rect.width;
              const y1 = currentDrawing.startY * rect.height;
              const x2 = currentDrawing.endX * rect.width;
              const y2 = currentDrawing.endY * rect.height;
              
              return (
                <svg
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: rect.width, height: rect.height }}
                >
                  <line
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke={currentDrawing.color}
                    strokeWidth="3"
                    opacity="0.8"
                  />
                </svg>
              );
            } else if (currentDrawing.type === 'rectangle') {
              const x = Math.min(currentDrawing.startX, currentDrawing.endX) * rect.width;
              const y = Math.min(currentDrawing.startY, currentDrawing.endY) * rect.height;
              const width = Math.abs(currentDrawing.endX - currentDrawing.startX) * rect.width;
              const height = Math.abs(currentDrawing.endY - currentDrawing.startY) * rect.height;
              
              return (
                <svg
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: rect.width, height: rect.height }}
                >
                  <rect
                    x={x} y={y} width={width} height={height}
                    fill="none"
                    stroke={currentDrawing.color}
                    strokeWidth="3"
                    opacity="0.8"
                  />
                </svg>
              );
            } else if (currentDrawing.type === 'sketch' && currentDrawing.points.length > 1) {
              const pathData = currentDrawing.points.map((point, index) => {
                const x = point.x * rect.width;
                const y = point.y * rect.height;
                return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
              }).join(' ');
              
              return (
                <svg
                  className="absolute inset-0 pointer-events-none"
                  style={{ width: rect.width, height: rect.height }}
                >
                  <path
                    d={pathData}
                    fill="none"
                    stroke={currentDrawing.color}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity="0.8"
                  />
                </svg>
              );
            }
            return null;
          })()}

          {/* 🏷️ Render tags for current frame */}
          {(tags[currentFrame] || []).map((tag, i) => {
  const rect = elementRef.current?.getBoundingClientRect();
  const left = rect ? (tag.ix * rect.width) : 0;
  const top = rect ? (tag.iy * rect.height) : 0;
  return (
    <div
      key={i}
      className="absolute flex flex-col items-center cursor-pointer group"
      style={{ left, top, transform: "translate(-50%, -100%)" }}
      onClick={(e) => {
        e.stopPropagation();
        handleRemoveTag(i);
      }}
      title="Click to remove"
    >
      <div className="w-3 h-3 bg-cyan-400 rounded-full border border-white group-hover:bg-red-400 transition-colors"></div>
      <div className="mt-1 text-xs bg-cyan-600 px-2 py-0.5 rounded group-hover:bg-red-600 transition-colors">
        {tag.label}
      </div>
    </div>
  );
})}


          {/* Input popup for adding tag */}
          {showTagInput && (
            <div
              className="absolute bg-gray-800 p-2 rounded-lg border border-cyan-500 shadow-lg"
              style={{
                left: tagPosition.x,
                top: tagPosition.y,
                transform: "translate(-50%, -120%)",
              }}
            >
              <input
                type="text"
                placeholder="Enter tag"
                className="px-2 py-1 text-black rounded w-32"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddTag(e.target.value);
                  if (e.key === "Escape") setShowTagInput(false);
                }}
                onBlur={(e) => handleAddTag(e.target.value)}
              />
            </div>
          )}
        </div>

        {Object.keys(metadata).length > 0 && (
          <div className="w-full lg:w-[550px] bg-gray-800/70 border border-gray-700 rounded-xl p-4 backdrop-blur-md shadow-inner overflow-y-auto max-h-[900px]">
            <h3 className="font-semibold text-cyan-400 mb-2 border-b border-gray-700 pb-1 sticky top-0 bg-gray-800/90 backdrop-blur-md z-10">
              DICOM Metadata
            </h3>
            <div className="grid grid-cols-1 gap-2 text-sm">
              {Object.entries(metadata).map(([key, value]) => (
                <div key={key}>
                  <span className="text-gray-400">{key}:</span>{" "}
                  <span className="font-medium text-white">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="text-red-400 mt-3 text-sm font-medium">⚠ {error}</div>
      )}

      {/* Enhanced Export Status Notifications */}
      {exportStatus && (
        <div className={`mt-4 p-4 rounded-xl border-2 text-sm font-medium shadow-lg backdrop-blur-sm ${
          exportStatus === 'success' 
            ? 'bg-green-900/60 border-green-400 text-green-200' 
            : exportStatus === 'error'
            ? 'bg-red-900/60 border-red-400 text-red-200'
            : 'bg-blue-900/60 border-blue-400 text-blue-200'
        }`}>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              {exportStatus === 'loading' && (
                <div className="flex items-center">
                  <span className="animate-spin text-lg">⏳</span>
                  <div className="ml-2 w-32 bg-blue-800 rounded-full h-2">
                    <div className="bg-blue-400 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
                  </div>
                </div>
              )}
              {exportStatus === 'success' && <span className="text-xl">✅</span>}
              {exportStatus === 'error' && <span className="text-xl">❌</span>}
            </div>
            <div className="flex-1">
              <div className={`font-semibold mb-1 ${
                exportStatus === 'success' ? 'text-green-100' :
                exportStatus === 'error' ? 'text-red-100' : 'text-blue-100'
              }`}>
                {exportStatus === 'loading' && 'Exporting DICOM...'}
                {exportStatus === 'success' && 'Export Successful!'}
                {exportStatus === 'error' && 'Export Failed'}
              </div>
              <div className="text-sm opacity-90 leading-relaxed">
                {exportMessage}
              </div>
              {exportStatus === 'success' && (
                <div className="mt-2 text-xs text-green-300 opacity-75">
                  The file has been downloaded to your default downloads folder.
                </div>
              )}
              {exportStatus === 'error' && (
                <div className="mt-2 text-xs text-red-300 opacity-75">
                  If the problem persists, try refreshing the page and reloading your DICOM file.
                </div>
              )}
            </div>
            {(exportStatus === 'success' || exportStatus === 'error') && (
              <button
                onClick={() => {
                  setExportStatus(null);
                  setExportMessage("");
                }}
                className="flex-shrink-0 text-gray-400 hover:text-white transition-colors"
                title="Dismiss notification"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      {/* Save Prompt Modal */}
      {showSavePrompt && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-gray-800 border border-cyan-500 rounded-xl p-6 max-w-md shadow-2xl">
            <h3 className="text-xl font-semibold text-cyan-400 mb-3">
              💾 Save Tagged Frames?
            </h3>
            <p className="text-gray-300 mb-5">
              You have unsaved tagged frames. Would you like to save them before playing?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => handleSavePromptResponse(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition"
              >
                Skip
              </button>
              <button
                onClick={() => handleSavePromptResponse(true)}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 rounded-lg transition"
              >
                Save All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      {numFrames > 1 && (
        <div className="mt-6 w-[900px] flex flex-col items-center gap-4 bg-gray-800/70 p-4 rounded-xl border border-gray-700 backdrop-blur-md">
          <div className="flex flex-wrap items-center justify-between w-full gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={handlePlay}
                className={`px-4 py-1.5 rounded-md font-medium transition ${
                  playing
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-cyan-600 hover:bg-cyan-700"
                }`}
              >
                {playing ? "⏸ Pause" : "▶ Play"}
              </button>
              <div className="text-gray-300 text-sm flex items-center gap-2">
                <span>Frame {currentFrame + 1} / {numFrames}</span>
                {tags[currentFrame] && tags[currentFrame].length > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="text-cyan-400">🏷️ {tags[currentFrame].length}</span>
                    {savedFrames.has(currentFrame) ? (
                      <span className="text-green-400 text-xs" title="Frame annotations saved">✓ Saved</span>
                    ) : (
                      <span className="text-yellow-400 text-xs animate-pulse" title="Frame has unsaved annotations">● Unsaved</span>
                    )}
                  </div>
                )}
                {(!tags[currentFrame] || tags[currentFrame].length === 0) && (
                  <span className="text-gray-500 text-xs">No annotations</span>
                )}
              </div>
              
              {/* Overall export status summary */}
              {Object.keys(tags || {}).length > 0 && (
                <div className="text-xs text-gray-400 border-l border-gray-600 pl-3">
                  {(() => {
                    const totalFramesWithTags = Object.keys(tags || {}).filter(
                      frameKey => tags[frameKey] && tags[frameKey].length > 0
                    ).length;
                    const totalSavedFrames = Object.keys(tags || {}).filter(
                      frameKey => tags[frameKey] && tags[frameKey].length > 0 && savedFrames.has(parseInt(frameKey))
                    ).length;
                    const totalAnnotations = Object.values(tags || {}).reduce(
                      (sum, frameTags) => sum + (frameTags ? frameTags.length : 0), 0
                    );
                    
                    return (
                      <div className="flex items-center gap-2">
                        <span>{totalAnnotations} annotations</span>
                        <span>•</span>
                        <span className={totalSavedFrames === totalFramesWithTags ? "text-green-400" : "text-yellow-400"}>
                          {totalSavedFrames}/{totalFramesWithTags} frames saved
                        </span>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="flex items-center gap-5 text-sm">
              <div className="flex flex-col items-center">
                <label className="text-gray-300">🔍 Zoom</label>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.1"
                  value={zoom}
                  onChange={handleZoomChange}
                  className="accent-cyan-500"
                />
                <span className="text-gray-400">{zoom.toFixed(1)}x</span>
              </div>

              <div className="flex flex-col items-center">
                <label className="text-gray-300">🎨 Contrast</label>
                <input
                  type="range"
                  min="100"
                  max="3000"
                  step="50"
                  value={contrast}
                  onChange={handleContrastChange}
                  className="accent-cyan-500"
                />
                <span className="text-gray-400">{contrast}</span>
              </div>

              <div className="flex flex-col items-center">
                <label className="text-gray-300">⚡ Speed</label>
                <input
                  type="range"
                  min="0.25"
                  max="2"
                  step="0.25"
                  value={speed}
                  onChange={handleSpeedChange}
                  className="accent-cyan-500"
                />
                <span className="text-gray-400">{speed.toFixed(2)}x</span>
              </div>
            </div>
          </div>

          <input
            type="range"
            min="0"
            max={numFrames - 1}
            value={currentFrame}
            onChange={handleSliderChange}
            className="w-full accent-cyan-500"
          />
        </div>
      )}
    </div>
  );
};

export default DicomViewer;